import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Alert severity levels
export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

// Alert types for business operations
export type AlertType =
  | 'system'        // System status, maintenance
  | 'booking'       // Booking-related alerts
  | 'payment'       // Payment processing alerts
  | 'availability'  // Inventory/availability alerts
  | 'promotion'     // Special offers, deals
  | 'security'      // Security-related alerts
  | 'maintenance'   // Scheduled maintenance
  | 'service';      // Service disruptions

// Structure for a single alert
export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  dismissible: boolean;
  autoHideDuration?: number;  // Duration in ms before auto-hiding, if applicable
  timestamp: string;
  link?: {
    text: string;
    url: string;
  };
}

// Global alerts state
export interface GlobalAlertsState {
  alerts: Alert[];
  maxAlerts: number;        // Maximum number of alerts to show at once
  suppressedTypes: AlertType[];  // Alert types user has chosen to suppress
  dismissedAlerts: string[];    // IDs of alerts that have been dismissed
  isLoading: boolean;          // Loading state for fetching alerts
  error: string | null;        // Error from fetching alerts
}

const initialState: GlobalAlertsState = {
  alerts: [
    {
      id: 'test-alert-id',
      type: 'system',
      severity: 'info',
      message: 'Test Alert',
      dismissible: true,
      timestamp: new Date().toISOString(),
    }
  ],
  maxAlerts: 5,
  suppressedTypes: [],
  dismissedAlerts: [],
  isLoading: false,
  error: null
};

// Async thunk to fetch global alerts from the server
export const fetchGlobalAlerts = createAsyncThunk(
  'globalAlerts/fetchGlobalAlerts',
  async () => {
    const response = await fetch('/api/global-alerts');
    const { alerts } = (await response.json()) as { alerts: Alert[] };
    return alerts;
  }
);

export const globalAlerts = createSlice({
  name: 'globalAlerts',
  initialState,
  reducers: {
    addAlert: (state, action: PayloadAction<Omit<Alert, 'id' | 'timestamp'>>) => {
      const newAlert: Alert = {
        ...action.payload,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString()
      };

      // Don't add if type is suppressed
      if (state.suppressedTypes.includes(newAlert.type)) {
        return;
      }

      // Remove oldest alert if we're at max capacity
      if (state.alerts.length >= state.maxAlerts) {
        state.alerts.shift();
      }

      state.alerts.push(newAlert);
    },

    removeAlert: (state, action: PayloadAction<string>) => {
      // Add to dismissed alerts if not already there
      if (!state.dismissedAlerts.includes(action.payload)) {
        state.dismissedAlerts.push(action.payload);
      }

      // Remove from active alerts
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },

    clearAlerts: (state) => {
      state.alerts = [];
    },

    suppressAlertType: (state, action: PayloadAction<AlertType>) => {
      if (!state.suppressedTypes.includes(action.payload)) {
        state.suppressedTypes.push(action.payload);
      }
    },

    unsuppressAlertType: (state, action: PayloadAction<AlertType>) => {
      state.suppressedTypes = state.suppressedTypes.filter(type => type !== action.payload);
    },

    setMaxAlerts: (state, action: PayloadAction<number>) => {
      state.maxAlerts = action.payload;
      // Trim alerts if new max is lower than current count
      if (state.alerts.length > action.payload) {
        state.alerts = state.alerts.slice(-action.payload);
      }
    },

    dismissAlert: (state, action: PayloadAction<string>) => {
      // Add to dismissed alerts if not already there
      if (!state.dismissedAlerts.includes(action.payload)) {
        state.dismissedAlerts.push(action.payload);
      }
    },

    clearDismissedAlerts: (state) => {
      state.dismissedAlerts = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalAlerts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGlobalAlerts.fulfilled, (state, action) => {
        state.isLoading = false;

        // Add new server alerts that aren't already in the store and aren't in dismissed state
        const existingAlertIds = new Set(state.alerts.map(alert => alert.id));
        const newAlerts = action.payload.filter(alert =>
          !existingAlertIds.has(alert.id) &&
          !state.dismissedAlerts.includes(alert.id)
        );

        // Add new alerts up to the max limit
        const availableSpace = state.maxAlerts - state.alerts.length;
        if (availableSpace > 0 && newAlerts.length > 0) {
          // Add alerts up to the available space or all new alerts
          const alertsToAdd = newAlerts.slice(0, availableSpace);
          state.alerts = [...state.alerts, ...alertsToAdd];
        }
      })
      .addCase(fetchGlobalAlerts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch alerts';
      });
  }
});

// Export actions
export const {
  addAlert,
  removeAlert,
  clearAlerts,
  suppressAlertType,
  unsuppressAlertType,
  setMaxAlerts,
  dismissAlert,
  clearDismissedAlerts
} = globalAlerts.actions;

// Export reducer
export default globalAlerts.reducer;

// Selectors
export const selectAllAlerts = (state: { globalAlerts: GlobalAlertsState }) =>
  state.globalAlerts.alerts;

export const selectActiveAlerts = (state: { globalAlerts: GlobalAlertsState }) =>
  state.globalAlerts.alerts.filter(alert =>
    !state.globalAlerts.suppressedTypes.includes(alert.type) &&
    !state.globalAlerts.dismissedAlerts.includes(alert.id)
  );

export const selectSuppressedTypes = (state: { globalAlerts: GlobalAlertsState }) =>
  state.globalAlerts.suppressedTypes;

export const selectMaxAlerts = (state: { globalAlerts: GlobalAlertsState }) =>
  state.globalAlerts.maxAlerts;

export const selectDismissedAlerts = (state: { globalAlerts: GlobalAlertsState }) =>
  state.globalAlerts.dismissedAlerts;
